import React, { FC, FormEvent, useCallback, useEffect, useState, ChangeEvent } from 'react'
import SignupForm, { SignupFormUserStub } from 'pages/_serverRendered/EventSignup/SignupForm'
import RealmLogo from 'components/MarketingSite/Header/RealmLogo'
import CoreInput from 'components/Core/CoreInput'
import CoreText from 'components/Core/CoreText'
import { InlineWidget, useCalendlyEventListener } from 'react-calendly'
import CoreButton from 'components/Core/CoreButton'
import { useAdjustBanner } from 'non-rendering/ShowOrHideAdjustBanner'
import {
  eventAdditionalNotesState,
  eventsAddedUserState,
  eventSelectedTeamMemberState,
  EventSignupMode,
  eventsSelectedMarketState,
  eventsSelectedModeState,
} from 'recoil/events'
import { RecoilRoot, useRecoilState } from 'recoil'
import QRCode from 'react-qr-code'
import { patchFormData } from 'utils/fetch'
import cx from 'classnames'

interface Props {
  events_team_members: string[] | null
  sales_events: { name: string; event_type: string | null; event_category: string | null }[] | null
  webview: boolean
  calendly_url: string | null
  calendly_url_for_rebuilds: string | null
  project_types: string[] | null
  sandbox?: boolean
  non_app_signup_type: string
  skip_meeting_prompt?: boolean
  allow_start_over?: boolean
  title?: string
}

const Inner: FC<Props> = ({
  events_team_members,
  calendly_url,
  calendly_url_for_rebuilds,
  sales_events,
  project_types,
  sandbox,
  non_app_signup_type,
  title,
  skip_meeting_prompt,
  allow_start_over = true,
}) => {
  const [mode, setMode] = useRecoilState(eventsSelectedModeState)
  const [alert, setAlert] = useState<{ type: 'error' | 'success'; message: string } | null>(null)
  const [user, setUser] = useRecoilState(eventsAddedUserState)
  const [eventScheduled, setEventScheduled] = useState(false)
  const [calendarType, setCalendarType] = useState<'normal' | 'rebuild'>('normal')
  const [notes, setNotes] = useRecoilState(eventAdditionalNotesState)

  const [eventName, setEventName] = useRecoilState(eventsSelectedMarketState)
  const [referrer, setReferrer] = useRecoilState(eventSelectedTeamMemberState)

  const { hideAdjustBanner } = useAdjustBanner()
  useEffect(() => hideAdjustBanner(), [hideAdjustBanner])

  useEffect(() => setNotes(''), [setNotes])

  const handleReset = useCallback(() => {
    setUser(null)
    setNotes('')
    setMode('user-info')
  }, [setUser, setMode, setNotes])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.has('n')) {
      handleReset()
    }
  }, [handleReset])

  const handleClickRenovation = useCallback(() => {
    setCalendarType('normal')
    setMode('schedule-meeting')
  }, [setMode])

  const handleClickRebuild = useCallback(() => {
    setCalendarType('rebuild')
    setMode('schedule-meeting')
  }, [setMode])

  const handleSwitchMode = useCallback(
    (value: EventSignupMode) => {
      return () => setMode(value)
    },
    [setMode]
  )

  const handleChangeNotes = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      setNotes(e.target.value)
    },
    [setNotes]
  )

  const handleSuccess = useCallback(
    (user: SignupFormUserStub) => {
      if (!user.email?.length) handleReset()
      else {
        setUser(user)
        if (!calendly_url) handleReset()
        else setMode(skip_meeting_prompt ? 'schedule-meeting' : 'prompt-meeting')
      }
    },
    [calendly_url, handleReset, setUser, setMode, skip_meeting_prompt]
  )

  const handleSubmitNotes = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      if (!user) return

      try {
        if (!sandbox) {
          await patchFormData(`/api/v1/users/signup_details`, new FormData(event.target as HTMLFormElement))
        }
        setAlert({ type: 'success', message: 'User notes saved!' })
      } catch (e) {
        console.error(e)
        setAlert({ type: 'error', message: 'Error saving user notes' })
      }

      allow_start_over ? handleReset() : setMode('thanks')
    },
    [user, handleReset, sandbox, allow_start_over, setMode]
  )

  const handleGoBackHome = useCallback(() => {
    handleReset()
    window.location.replace('/')
  }, [handleReset])

  useEffect(() => {
    if (window) window.scrollTo(0, 0)
  }, [user, mode])

  useEffect(() => {
    if (!user && mode != 'user-info') setMode('user-info')
    if (!!user && mode == 'user-info') setMode('prompt-meeting')
  }, [user, mode, setMode])

  useEffect(() => {
    if (!sales_events || sales_events.some((x) => x && x.name == eventName) == false) setEventName('')
    if (events_team_members?.includes(referrer) == false) setReferrer('')
  }, [setEventName, sales_events, eventName, events_team_members, referrer, setReferrer])

  const handleEventScheduled = useCallback(() => {
    setEventScheduled(true)
  }, [])

  useEffect(() => {
    setEventScheduled(false)
  }, [user])

  useCalendlyEventListener({
    onEventScheduled: () => handleEventScheduled(),
  })

  return (
    <div
      className={cx('tw:flex tw:flex-col tw:min-h-screen tw:bg-gradient-to-b', {
        'tw:from-green-900 tw:to-neutrals-1000': !sandbox,
        'tw:from-red-900 tw:to-neutrals-1000': !!sandbox,
      })}
    >
      {sandbox && (
        <div className="tw:p-4 tw:bg-red-700 tw:text-white tw:text-sm">
          {'You are in sandbox mode. Users will not be created.'}
        </div>
      )}
      {user && calendly_url && mode == 'schedule-meeting' ? (
        <div className="tw:flex tw:flex-col tw:h-dvh">
          <InlineWidget
            url={`${calendarType == 'normal' ? calendly_url : calendly_url_for_rebuilds}?hide_gdpr_banner=1`}
            styles={{ width: '100vw', flexGrow: 1 }}
            prefill={{ email: user.email, name: user.full_name, customAnswers: { a1: user.phone_number } }}
            utm={{ utmSource: 'event-signup', utmMedium: `original-email:${user.email}` }}
          />
          <div className="tw:flex tw:gap-2 tw:border-t tw:p-2 tw:bg-white">
            {allow_start_over && (
              <CoreButton text="Start over" kind="secondary" size="s" onClick={handleReset} className="tw:px-4" />
            )}
            {eventScheduled && (
              <CoreButton
                text="Add additional notes"
                kind="secondary"
                size="s"
                onClick={handleSwitchMode('user-notes')}
                className="tw:px-4"
              />
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="tw:flex tw:py-6 tw:sm:y-8 tw:justify-center tw:items-center">
            <RealmLogo className="tw:w-20 tw:sm:w-24 tw:text-white" />
          </div>
          <div className="tw:bg-neutrals-100 tw:mb-16 tw:space-y-6 tw:mx-2 tw:sm:mx-auto tw:p-4 tw:sm:p-8 tw:rounded-3xl tw:sm:rounded-5xl tw:sm:w-sm">
            {!user ? (
              <SignupForm
                alert={alert}
                setAlert={setAlert}
                sandbox={sandbox}
                hideNotes
                onSuccess={handleSuccess}
                testMarketplace
                header={
                  <div className={cx('tw:space-y-4', { 'tw:pb-4 tw:border-b': sales_events || events_team_members })}>
                    <CoreText.Headline
                      className="tw:text-center tw:text-4xl tw:sm:text-left tw:mb-8 tw:mt-4 tw:sm:mt-0"
                      size="xs"
                    >
                      {title || 'Event Signup'}
                    </CoreText.Headline>
                    {!!sales_events && (
                      <CoreInput.Select
                        name="user[sfdc_lead_attrs][Name_of_market__c]"
                        label="Name of event"
                        required
                        value={eventName}
                        onChangeText={setEventName}
                        allowBlank
                      >
                        {sales_events.map((event) => (
                          <option key={event.name} value={event.name}>
                            {event.name}
                          </option>
                        ))}
                      </CoreInput.Select>
                    )}
                    {!!events_team_members && (
                      <CoreInput.Select
                        value={referrer}
                        onChangeText={setReferrer}
                        name="user[sfdc_lead_attrs][Lead_Captured_By__c]"
                        label="Referred by"
                        required
                        allowBlank
                      >
                        {events_team_members.map((teamMember) => (
                          <option key={teamMember} value={teamMember}>
                            {teamMember}
                          </option>
                        ))}
                      </CoreInput.Select>
                    )}
                  </div>
                }
                className="tw:space-y-4"
                addressOptional
                emailOptional
              >
                <input type="hidden" name="bypass_signin" value="true" />
                <input
                  type="hidden"
                  name="user[non_app_signup_type]"
                  value={non_app_signup_type || 'non-homeshow-event'}
                />
                <CoreInput.TextArea
                  value={notes}
                  onChange={handleChangeNotes}
                  name="user[signup_message]"
                  label="Anything else you want to share?"
                />
              </SignupForm>
            ) : mode == 'prompt-download' ? (
              <div className="tw:flex tw:flex-col tw:gap-8 tw:items-center">
                <CoreText.Headline size="xs">
                  {'Download our award-winning app to save time and money on your renovations!'}
                </CoreText.Headline>
                <QRCode value={`https://realmhome.com/mr/client-onboarding/${user.email}/default`} />
                {allow_start_over && (
                  <CoreButton className="tw:w-full" text="Skip" onClick={handleReset} kind="secondary" />
                )}
              </div>
            ) : mode == 'prompt-meeting' ? (
              <div className="tw:space-y-4">
                <CoreText.Headline size="xs" className="tw:mb-8">
                  {'Do you want to schedule a meeting?'}
                </CoreText.Headline>
                <CoreButton className="tw:w-full" text="Yes" onClick={handleClickRenovation} kind="primary" />
                {!!calendly_url_for_rebuilds && (
                  <CoreButton
                    className="tw:w-full"
                    text="Wildfire Rebuild"
                    onClick={handleClickRebuild}
                    kind="significant"
                  />
                )}
                <CoreButton
                  className="tw:w-full"
                  text="Skip"
                  onClick={handleSwitchMode('prompt-notes')}
                  kind="secondary"
                />
              </div>
            ) : mode == 'thanks' ? (
              <div className="tw:space-y-6">
                <CoreText.Headline size="xs">{'Thank you'}</CoreText.Headline>
                <CoreText.Paragraph size="l" weight="light">
                  {'Thank you for choosing Realm, our team will be in touch to get started.'}
                </CoreText.Paragraph>
                <CoreButton kind="secondary" className="tw:px-8" onClick={handleGoBackHome} text="Go back home" />
              </div>
            ) : mode == 'prompt-notes' ? (
              <div className="tw:space-y-4">
                <CoreText.Headline size="xs" className="tw:mb-8">
                  {'Do you want to leave additional notes or set the project type?'}
                </CoreText.Headline>
                <CoreButton className="tw:w-full" text="Yes" onClick={handleSwitchMode('user-notes')} kind="primary" />
                <CoreButton
                  className="tw:w-full"
                  text="Skip"
                  onClick={handleSwitchMode('prompt-download')}
                  kind="secondary"
                />
              </div>
            ) : mode == 'user-notes' && !!user ? (
              <form onSubmit={handleSubmitNotes} className="tw:space-y-4">
                <input type="hidden" name="user[email]" value={user.email} />
                <CoreText.Headline
                  className="tw:text-center tw:text-4xl tw:sm:text-left tw:mb-8 tw:mt-4 tw:sm:mt-0"
                  size="xs"
                >
                  {`Notes for ${user.full_name}`}
                </CoreText.Headline>
                {!!project_types && (
                  <CoreInput.Select name="user[sfdc_lead_attrs][Project_Type__c][]" multiple label="Project types">
                    {project_types.map((projectType) => (
                      <option key={projectType} value={projectType}>
                        {projectType}
                      </option>
                    ))}
                  </CoreInput.Select>
                )}
                <CoreInput.TextArea
                  value={notes}
                  onChange={handleChangeNotes}
                  name="user[signup_message]"
                  label="Anything else you want to share?"
                />
                <CoreButton type="submit" text="Submit" className="tw:w-full" />
              </form>
            ) : null}
          </div>
          <div className="tw:grow" />
        </>
      )}
    </div>
  )
}

const EventSignup: FC<Props> = (props) => {
  return (
    <RecoilRoot>
      <Inner {...props} />
    </RecoilRoot>
  )
}

export default EventSignup
